import * as React from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { Button } from '@/components/ui/button'
import { useMobileMenu } from '@/hooks/useMobileMenu'

interface MobileSlideInMenuProps {
  isOpen: boolean
  onClose: () => void
  mode?: 'bottom' | 'top'
  children?: React.ReactNode
  id: string
  heading?: string // Optional custom heading to override default
}

export function MobileSlideInMenu({
  isOpen,
  onClose,
  mode = 'bottom',
  children,
  id,
  heading,
}: MobileSlideInMenuProps) {
  const menuId = React.useId() // Generate unique ID if not provided
  const finalMenuId = id || menuId
  const { openMenus, openMenu, closeMenu } = useMobileMenu()

  // Create a stable close function that can be registered with the context
  const closeFn = React.useCallback(() => {
    onClose()
  }, [onClose])

  // Determine if this menu should be open based on context state
  const isMenuOpen = openMenus.has(finalMenuId) && isOpen

  // Register menu when it opens and close all others
  React.useEffect(() => {
    if (isOpen) {
      openMenu(finalMenuId, closeFn)
    }
  }, [isOpen, finalMenuId, openMenu, closeFn])

  // Close menu when component unmounts
  React.useEffect(() => {
    return () => {
      closeMenu(finalMenuId)
    }
  }, [finalMenuId, closeMenu])

  // Handle the close action - close this menu and call onClose
  const handleClose = React.useCallback(() => {
    closeMenu(finalMenuId)
    onClose()
  }, [closeMenu, finalMenuId, onClose])

  // Add/remove no-scroll class on body when menu opens/closes
  React.useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('no-scroll')
    } else {
      document.body.classList.remove('no-scroll')
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('no-scroll')
    }
  }, [isMenuOpen])

  if (!isMenuOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed z-40 bg-black/20 backdrop-blur-sm lg:hidden animate-in fade-in duration-200"
        style={{
          ...(mode === 'top'
            ? {
                top: 'calc(4rem + env(safe-area-inset-top))', // Don't cover header
                left: 0,
                right: 0,
                bottom: 0,
              }
            : {
                top: 0,
                left: 0,
                right: 0,
                bottom: 'calc(4rem + env(safe-area-inset-bottom))', // Don't cover bottom nav
              }),
        }}
        onClick={handleClose}
      />

      {/* Menu Sheet */}
      <div
        className={cn(
          'fixed left-0 right-0 z-50 lg:hidden animate-in duration-300',
          mode === 'top'
            ? 'slide-in-from-top' // Slide down from header
            : 'slide-in-from-bottom' // Slide up from bottom
        )}
        style={{
          ...(mode === 'top'
            ? {
                top: 'calc(4rem + env(safe-area-inset-top))', // Position beneath header
                maxHeight:
                  'calc(100vh - 5rem - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
              }
            : {
                bottom: 'calc(4rem + env(safe-area-inset-bottom))', // Position above bottom nav
                maxHeight: 'calc(100vh - 5rem - env(safe-area-inset-bottom))',
              }),
        }}
      >
        <div
          className={cn(
            'bg-background border-b',
            mode === 'top' ? 'rounded-b-3xl' : 'rounded-t-3xl border-t'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            {heading && <h2 className="text-lg font-semibold">{heading}</h2>}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-8 w-8 rounded-full"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="px-4 py-2 max-h-[70vh] overflow-y-auto">{children}</div>
        </div>
      </div>
    </>
  )
}
